import { Injectable } from "@angular/core";

type CommandFunction<T = void> = (context: Excel.RequestContext) => Promise<void> | void;
type PostSyncFunction<T = void> = (context: Excel.RequestContext) => T;

interface IQueuedCommand<T> {
  command: CommandFunction<T>;
  postSync?: PostSyncFunction<T>;
  resolve?: (value: T | PromiseLike<T>) => void;
  reject?: (reason?: any) => void;
}

@Injectable({ providedIn: 'root' })
export class ExcelCommandQueueService {
  private queue: IQueuedCommand<any>[] = [];
  private isFlushing = false;
  private readonly batchSize = 10;

  enqueue<T = void>(
    command: CommandFunction<T>,
    postSync?: PostSyncFunction<T>
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({ command, postSync, resolve, reject });
      queueMicrotask(() => this.flushIfNeeded());
    });
  }

  private async flushIfNeeded(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0) return;

    this.isFlushing = true;
    const commandsToRun = this.queue.splice(0, this.batchSize);

    try {
      await Excel.run(async (context) => {
        for (const { command } of commandsToRun) {
          await command(context);
        }
        await context.sync();

        for (const { postSync, resolve } of commandsToRun) {
          if (resolve) {
            try {
              const result = postSync ? postSync(context) : undefined;
              resolve(result as any);
            } catch (err) {
              resolve(undefined as any);
            }
          }
        }
      });
    } catch (error) {
      for (const { reject } of commandsToRun) {
        reject?.(error);
      }
    } finally {
      this.isFlushing = false;
      if (this.queue.length > 0) {
        queueMicrotask(() => this.flushIfNeeded());
      }
    }
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    if (this.isFlushing) {
      await new Promise(resolve => setTimeout(resolve, 50));
      return this.flush();
    }
    await this.flushIfNeeded();
  }

  /**
   * To minimize the excel round trips. Waits for any in-progress flush to complete 
   * and receives the RequestContext directly and may call context.sync() as many times as needed
   */
  async runWithContext(operation: (context: Excel.RequestContext) => Promise<void>): Promise<void> {
    while (this.isFlushing) {
      await new Promise<void>(resolve => setTimeout(resolve, 10));
    }

    this.isFlushing = true;
    try {
      await Excel.run(operation);
    } finally {
      this.isFlushing = false;
      if (this.queue.length > 0) {
        queueMicrotask(() => this.flushIfNeeded());
      }
    }
  }
}
